<template>
    <v-app id="app">
        <!-- App bar -->
        <v-app-bar app clipped-right color="bal-gray" light flat>
            <v-app-bar-nav-icon large @click.stop="toggleDrawer" />
            <v-toolbar-title
                >{{ `${$route.meta.title}` | t
                }}<span style="color: gray">{{ ` (${productCount})` }}</span></v-toolbar-title
            >
            <v-combobox
                v-if="hasPermissionForAction(PermissionsConstants.PRODUCT, PermissionsConstants.SEARCH)"
                :items="searchHistory"
                v-model="searchChips"
                @input="controlSearchString($event)"
                item-text="keyword"
                id="searchInput"
                class="mt-6 mx-2"
                :placeholder="t('Product name, code, family, category')"
                prepend-inner-icon="search"
                flat
                solo
                dense
                clearable
                multiple
                chips
                deletable-chips
                small-chips
            >
                <template slot="item" slot-scope="data">
                    <span @click.stop="addChip(data.item.keyword)" style="width: 100%; height: 100%" class="pa-2">
                        {{ data.item.keyword }}
                    </span>
                </template>
            </v-combobox>
            <v-spacer v-else />

            <v-menu
                bottom
                left
                offset-y
                v-if="
                    !exportMode &&
                    !priceMode &&
                    hasPermissionForAction(PermissionsConstants.PRODUCT, PermissionsConstants.UPLOAD)
                "
            >
                <template v-slot:activator="{ on }">
                    <v-btn
                        v-on="on"
                        class="px-2 ml-2 mx-2"
                        color="bal-dark-gray"
                        dark
                        style="min-width: 0"
                        :title="t('Upload product excel')"
                    >
                        <v-icon>{{ "fas fa-file-export" }}</v-icon>
                    </v-btn>
                </template>
                <v-list style="width: 160px">
                    <v-list-item>
                        <v-file-input
                            show-size
                            hide-details
                            dense
                            accept=".xlsx, .xls"
                            :label="'Upload' | t"
                            v-model="fileUploadValue"
                            @change="uploadExcel($event)"
                        />
                    </v-list-item>
                </v-list>
            </v-menu>
            <v-btn
                dark
                :color="priceMode ? 'bal-green' : 'bal-dark-gray'"
                @click="togglePriceMode"
                v-if="
                    hasPermissionForAction(PermissionsConstants.PRODUCT, PermissionsConstants.CHANGE_PRICE) &&
                    !exportMode
                "
                class="px-2 mr-2"
                style="min-width: 0"
            >
                <v-icon>fal fa-money-bill</v-icon>
            </v-btn>
            <v-btn
                dark
                color="bal-red"
                class="px-2"
                @click.stop="openNewProductsPriceModal"
                :color="exportProducts.length === 0 ? 'bal-dark-gray' : 'bal-red'"
                style="min-width: 0"
                v-if="
                    priceMode && hasPermissionForAction(PermissionsConstants.PRODUCT, PermissionsConstants.CHANGE_PRICE)
                "
            >
                {{ t("Set price") }}
                <span class="ml-1" v-if="priceMode || exportMode">{{ `(${selectedItemsCount})` }}</span>
                <v-icon>arrow_right</v-icon>
            </v-btn>
            <v-btn
                v-if="exportMode && !priceMode"
                dark
                color="bal-dark-gray"
                class="px-2"
                @click="toggleExportModal"
                style="min-width: 0"
            >
                <v-icon>fal fa-cog</v-icon>
            </v-btn>
            <v-btn
                dark
                color="bal-dark-gray"
                v-if="
                    !exportMode &&
                    !priceMode &&
                    hasPermissionForAction(PermissionsConstants.PRODUCT, PermissionsConstants.FILTER)
                "
                class="px-2"
                @click.stop="toggleListSettingsModal"
                style="min-width: 0"
            >
                <v-icon>settings</v-icon>
            </v-btn>

            <v-btn
                :color="exportMode ? 'bal-green' : 'bal-dark-gray'"
                dark
                class="ml-2"
                @click="toggleExportMode"
                v-if="!priceMode && hasPermissionForAction(PermissionsConstants.PRODUCT, PermissionsConstants.EXPORT)"
            >
                <v-icon size="small" class="mr-2">fa fa-file-export</v-icon>
                {{ "Export" | t }}
                <span v-if="priceMode || exportMode">{{ ` (${selectedItemsCount})` }}</span>
            </v-btn>
            <v-btn
                dark
                color="bal-red"
                class="px-2 ml-2"
                @click.stop="openNewProductDrawer"
                style="min-width: 0"
                v-if="
                    !exportMode &&
                    !priceMode &&
                    hasPermissionForAction(PermissionsConstants.PRODUCT, PermissionsConstants.CREATE)
                "
            >
                <v-icon>add</v-icon>
            </v-btn>
        </v-app-bar>

        <!-- Drawer left -->
        <main-drawer :drawer-open="mainDrawerOpen" @close="toggleDrawer" />

        <!--    Right drawer    -->
        <v-navigation-drawer :value="drawerOpen" fixed right hide-overlay width="900">
            <product-view v-if="$route.name === 'product-view'"></product-view>
            <product-edit v-if="$route.name === 'product-edit'"></product-edit>
        </v-navigation-drawer>

        <!-- Main content -->
        <v-main>
            <router-view :ref="$route.name === 'product-list' ? 'childComponent' : null" />
        </v-main>

        <!-- Export modal -->
        <product-export-modal />

        <!-- Modal where you can bulk edit prices -->
        <products-bulk-price-edit-modal />

        <!-- Settings modal -->
        <product-list-settings-modal />

        <!-- Price modal -->
        <product-price-modal v-if="priceModal" />

        <!-- Footer -->
        <page-footer />

        <!-- Success snackbar -->
        <v-snackbar color="success" bottom :timeout="2500" :value="requestSuccess">
            <div style="display: flex; flex-direction: row">
                <v-icon color="#FFF">check</v-icon>
                <div class="title pa-2">{{ "Success" | t }}</div>
            </div>
        </v-snackbar>

        <!-- Error snackbar -->
        <v-snackbar color="error" bottom :timeout="2500" :value="requestError">
            <div style="display: flex; flex-direction: row">
                <v-icon color="#FFF">error</v-icon>
                <div class="title pa-2">{{ errorMessage }}</div>
            </div>
        </v-snackbar>
    </v-app>
</template>
<script>
import ProductView from "../pages/products/product-view.vue";
import ProductEdit from "../pages/products/product-edit.vue";
import ProductExportModal from "../pages/products/product-export-modal.vue";
import ProductListSettingsModal from "../pages/products/product-list-settings-modal";
import ProductPriceModal from "../pages/products/product-price-modal";
import ProductsBulkPriceEditModal from "../pages/products/products-bulk-price-edit-modal";

import MainDrawer from "../components/main-drawer.vue";
import PageFooter from "../components/page-footer.vue";

import { mapGetters, mapState, mapMutations, mapActions } from "vuex";
import Store from "../store";

import { PermissionsConstants } from "@/permissions";

export default {
    components: {
        ProductView,
        ProductEdit,
        ProductExportModal,
        ProductListSettingsModal,
        ProductPriceModal,
        ProductsBulkPriceEditModal,
        PageFooter,
        MainDrawer,
    },
    data() {
        return {
            searchChips: [],
            mainDrawerOpen: null,
            fileUploadValue: null,
        };
    },
    async created() {
        await Store.dispatch("SearchHistoryStore/getSearchHistory", this.$route.path.replace("/", ""));
    },
    async mounted() {
        document.addEventListener("keydown", this.handleKeyboardListener);
        const drawerState = localStorage.getItem("mainDrawerOpen");
        if (drawerState) {
            this.mainDrawerOpen = JSON.parse(drawerState);
        } else {
            this.mainDrawerOpen = true;
        }
    },
    destroyed() {
        if (this.searchProductStrings.length > 0) Store.commit("ProductStore/resetProductSearch");
        document.removeEventListener("keydown", this.handleKeyboardListener);
        Store.commit("ProductStore/setRequestSuccess", false);
        Store.commit("ProductStore/setErrorMessage", "");
    },
    methods: {
        ...mapMutations("ProductStore", [
            "toggleExportMode",
            "togglePriceMode",
            "toggleExportModal",
            "resetActiveProduct",
            "toggleListSettingsModal",
            "toggleProductsBulkPriceEditModal",
        ]),
        ...mapActions("ProductStore", ["importProductsExcel"]),
        ...mapActions("SearchHistoryStore", ["getSearchHistory"]),
        submitSearch() {
            Store.commit("ProductStore/updateProductSearch", {
                term: this.searchChips,
            });
        },
        addChip(keyword) {
            if (!this.searchChips.includes(keyword)) {
                this.searchChips = [...this.searchChips, keyword];
                this.submitSearch();
            }
        },
        openNewProductDrawer() {
            this.$router.push({ name: "product-edit", params: { productId: "new" } });
        },
        toggleDrawer() {
            this.mainDrawerOpen = !this.mainDrawerOpen;
            localStorage.setItem("mainDrawerOpen", this.mainDrawerOpen);
        },
        handleKeyboardListener(e) {
            if (e.keyCode === 27 && this.drawerOpen) {
                this.resetActiveProduct();
                this.$router.push({ name: "product-list" });
            } else if (e.keyCode === 27 && this.exportMode && !this.exportModal) {
                Store.commit("ProductStore/toggleExportMode");
            } else if (e.keyCode === 27 && this.exportMode && this.exportModal) {
                Store.commit("ProductStore/toggleExportModal");
            } else if (e.keyCode === 13 && document.activeElement.tagName === "INPUT") {
                this.submitSearch();
            }
            if (
                this.activeProduct &&
                Object.keys(this.activeProduct).length > 0 &&
                this.activeProduct.id !== "new" &&
                this.$route.name !== "product-edit"
            ) {
                let currentProductIndex = this.products.findIndex((p) => p.id === this.activeProduct.id);
                if (e.key === "ArrowUp") {
                    if (currentProductIndex !== 0) {
                        const newProductId = this.products[currentProductIndex - 1].id;
                        Store.dispatch("ProductStore/getProductData", newProductId);
                        this.$router.push({ name: this.$route.name, params: { productId: newProductId } });
                    }
                } else if (e.key === "ArrowDown") {
                    if (currentProductIndex + 1 !== this.products.length) {
                        const newProductId = this.products[currentProductIndex + 1].id;
                        Store.dispatch("ProductStore/getProductData", newProductId);
                        this.$router.push({ name: this.$route.name, params: { productId: newProductId } });
                    }
                }
            }
        },
        controlSearchString(value) {
            if (value == "") {
                Store.dispatch("SearchHistoryStore/getSearchHistory", this.$route.path.replace("/", ""));
                Store.commit("ProductStore/updateProductSearch", { term: "" });
                return;
            }
            this.submitSearch();
        },
        async uploadExcel(e) {
            if (e) {
                await this.importProductsExcel(e);
                this.fileUploadValue = null;
            }
        },
        async openNewProductsPriceModal() {
            if (this.exportProducts.length === 0) return;
            this.toggleProductsBulkPriceEditModal();
        },
    },
    computed: {
        ...mapGetters(["hasPermissionForAction"]),
        PermissionsConstants() {
            return PermissionsConstants;
        },
        ...mapState("ProductStore", [
            "drawerOpen",
            "exportMode",
            "priceMode",
            "exportModal",
            "products",
            "activeProduct",
            "requestSuccess",
            "requestError",
            "errorMessage",
            "listSettingsModal",
            "priceModal",
            "exportProducts",
            "searchProductStrings",
        ]),
        ...mapState(["user"]),
        ...mapState("SearchHistoryStore", ["searchHistory"]),
        ...mapGetters("ProductStore", ["productCount"]),
        selectedItemsCount() {
            if (this.$refs.childComponent) {
                return this.$refs.childComponent.selectedItemCount();
            }
            return null
        },
    },
};
</script>

<style>
.v-input__icon--prepend-inner .v-icon {
    margin-top: 0px !important;
}

.theme--light.v-data-table .v-data-table-header th.sortable.active .v-data-table-header__icon {
    color: rgba(255, 0, 0, 0.8) !important;
}

#searchInput {
    padding: 10px !important;
}
</style>
